import * as React from "react";

import { cn } from "../../lib/utils.js";

/** Marks an element as a menu item the highlight can land on. */
export const MENU_ITEM_ATTR = "data-menu-item";
const ITEM_SELECTOR = `[${MENU_ITEM_ATTR}]:not([data-disabled]):not([aria-disabled="true"]):not(:disabled)`;
const PRESS_MS = 280;

/**
 * Smooth highlight for any list of menu items: one pill glides between items
 * as the pointer or keyboard focus moves (instead of each row snapping its own
 * background on), and pulses when an item is pressed. Items opt in with
 * `data-menu-item`. Styles: .menu-highlight in styles/tokens.css.
 *
 * Spread `props` on the container (it becomes `relative isolate`) and render
 * `indicator` as its first child. Works with any item markup, including Radix
 * Select items (they take focus when highlighted, which moves the pill).
 */
export function useMenuHighlight<T extends HTMLElement = HTMLDivElement>() {
  const containerRef = React.useRef<T | null>(null);
  const pressTimer = React.useRef(0);

  React.useEffect(() => () => window.clearTimeout(pressTimer.current), []);

  const itemFrom = (target: EventTarget | null): HTMLElement | null => {
    const container = containerRef.current;
    if (!container || !(target instanceof Element)) return null;
    const item = target.closest<HTMLElement>(ITEM_SELECTOR);
    return item && container.contains(item) ? item : null;
  };

  const place = (item: HTMLElement | null) => {
    const c = containerRef.current;
    if (!c) return;
    if (!item) {
      delete c.dataset.mhVisible;
      return;
    }
    // Appearing from hidden: jump straight to the item and only fade in, so
    // the pill never slides in from wherever it was last hidden.
    if (c.dataset.mhVisible === undefined) c.dataset.mhInstant = "";
    // The pill's box is the hovered item's measured position and size -- a
    // runtime DOM measurement, so it can't be a static class (CLAUDE.md
    // "JS-driven continuous animation values" exception).
    const cr = c.getBoundingClientRect();
    const ir = item.getBoundingClientRect();
    c.style.setProperty("--mh-x", `${ir.left - cr.left - c.clientLeft + c.scrollLeft}px`);
    c.style.setProperty("--mh-y", `${ir.top - cr.top - c.clientTop + c.scrollTop}px`);
    c.style.setProperty("--mh-w", `${ir.width}px`);
    c.style.setProperty("--mh-h", `${ir.height}px`);
    c.dataset.mhVisible = "";
    if (c.dataset.mhInstant !== undefined) {
      requestAnimationFrame(() => requestAnimationFrame(() => { delete c.dataset.mhInstant; }));
    }
  };

  const focusedItem = () => itemFrom(document.activeElement);

  const props = {
    ref: containerRef,
    onPointerOver: (e: React.PointerEvent<T>) => {
      const item = itemFrom(e.target);
      if (item) place(item);
    },
    onPointerLeave: () => place(focusedItem()),
    onFocus: (e: React.FocusEvent<T>) => place(itemFrom(e.target)),
    onBlur: (e: React.FocusEvent<T>) => {
      if (!containerRef.current?.contains(e.relatedTarget as Node | null)) place(null);
    },
    onPointerDown: (e: React.PointerEvent<T>) => {
      const c = containerRef.current;
      const item = itemFrom(e.target);
      if (!c || !item) return;
      place(item);
      // Restart the pulse even on rapid repeat presses.
      delete c.dataset.mhPress;
      void c.offsetWidth;
      c.dataset.mhPress = "";
      window.clearTimeout(pressTimer.current);
      pressTimer.current = window.setTimeout(() => { delete c.dataset.mhPress; }, PRESS_MS);
    },
  };

  const indicator = <span aria-hidden="true" className="menu-highlight" />;

  return { props, indicator };
}

export interface MenuListProps extends React.HTMLAttributes<HTMLDivElement> {}

/** A plain container with the sliding highlight -- put `data-menu-item` elements inside. */
export function MenuList({ className, children, ...rest }: MenuListProps) {
  const { props, indicator } = useMenuHighlight<HTMLDivElement>();
  return (
    <div {...rest} {...props} className={cn("relative isolate", className)}>
      {indicator}
      {children}
    </div>
  );
}
